import { useEffect, useMemo, useState } from 'react';

import { useMy } from '@/hooks/useMy';
import { JOBS_CRON, ScanSchedule } from '@/types';
import { getCronRelativeTime, mToMs } from '@/utils/date.util';

export const useNextRun = ({ email }: { email?: string } = {}) => {
  const [nextRunText, setNextRunText] = useState<string>('');
  const { data: settings, status: settingsStatus } = useMy(
    {
      resource: 'setting',
    },
    {
      email,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  const cron = useMemo(() => {
    if (settingsStatus === 'success') {
      const scanSchedule = settings?.find(s => s.name === 'schedule')?.value as
        | ScanSchedule
        | undefined;

      // If the settings are not present, use the default cron
      return scanSchedule
        ? `${scanSchedule?.start?.minute} ${scanSchedule?.start?.hour} * * ${scanSchedule?.days?.join?.(',')}`
        : JOBS_CRON;
    }
    return '';
  }, [settings, settingsStatus]);

  async function getNextRunText(cron: string) {
    try {
      const nextRun = await getCronRelativeTime(cron);
      const days = Math.floor(nextRun.hours / 24);
      const hours = nextRun.hours % 24;
      const minutes = nextRun.minutes;

      if (days === 0 && hours === 0 && minutes === 0) {
        setNextRunText('Next run now');
        return;
      }

      const parts: string[] = [];

      if (days > 0) {
        parts.push(`${days} day${days === 1 ? '' : 's'}`);
      }

      if (hours > 0) {
        parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
      }

      if (minutes > 0) {
        parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
      }

      let text = 'Next run in ';
      if (parts.length === 1) {
        text += parts[0];
      } else if (parts.length === 2) {
        text += `${parts[0]} and ${parts[1]}`;
      } else if (parts.length === 3) {
        text += `${parts[0]}, ${parts[1]} and ${parts[2]}`;
      }

      setNextRunText(text);
    } catch (error) {
      console.error('Error calculating next run time:', error);
      setNextRunText('Unable to calculate next run');
    }
  }

  // Update the next run time every 1 minute
  useEffect(() => {
    if (cron) {
      getNextRunText(cron);

      const interval = setInterval(() => {
        getNextRunText(cron);
      }, mToMs(1));

      return () => clearInterval(interval);
    }
  }, [cron]);

  return { nextRunText };
};
